import React, { useEffect } from 'react';
import { X } from 'lucide-react';

type ModalSize = 'sm' | 'md' | 'lg';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: ModalSize;
  /** Block backdrop click and Escape while a save is in flight. */
  isBusy?: boolean;
  /** ID of the heading element for aria-labelledby. */
  labelledBy?: string;
  /** Hide the close (X) button in the corner. */
  hideCloseButton?: boolean;
}

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: 'max-w-[26rem]',
  md: 'max-w-[32rem]',
  lg: 'max-w-[42rem]',
};

export const Modal: React.FC<ModalProps> & {
  Header: typeof ModalHeader;
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
} = ({
  isOpen,
  onClose,
  children,
  size = 'md',
  isBusy = false,
  labelledBy,
  hideCloseButton = false,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isBusy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, isBusy, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex animate-modal-fade items-end justify-center bg-[rgba(0,0,0,0.4)] sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isBusy) onClose();
      }}
    >
      <div
        className={`relative flex w-full max-h-[min(90vh,720px)] animate-modal-rise flex-col gap-6 overflow-y-auto overflow-x-hidden rounded-t-[1.75rem] bg-surface px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] pt-4 font-body text-on-surface shadow-[0_-8px_32px_rgba(28,27,25,0.12)] sm:rounded-[1.75rem] sm:border sm:border-solid sm:border-outline-variant sm:px-8 sm:pb-6 sm:pt-7 ${SIZE_CLASS[size]}`}
      >
        {/* Bottom-sheet grabber, as on mobile (phone widths only). */}
        <span aria-hidden className="mx-auto -mb-3 block h-1 w-10 shrink-0 rounded-full bg-outline-variant sm:hidden" />
        {!hideCloseButton && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            disabled={isBusy}
            className="absolute right-4 top-4 inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-none bg-surface-container-low text-on-surface transition-[transform,background] duration-150 hover:enabled:bg-surface-container active:scale-90 disabled:cursor-not-allowed disabled:opacity-30 max-sm:hidden"
          >
            <X size={16} />
          </button>
        )}
        {children}
      </div>
    </div>
  );
};

interface ModalHeaderProps {
  eyebrow?: string;
  title: string;
  titleId?: string;
  subtitle?: string;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({ eyebrow, title, titleId, subtitle }) => (
  <header className="flex flex-col gap-2">
    {eyebrow && (
      <p className="pb-eyebrow m-0">
        {eyebrow}
      </p>
    )}
    <h2
      id={titleId}
      className="pb-heading-2 m-0 pr-10"
    >
      {title}
    </h2>
    {subtitle && (
      <p className="m-0 text-sm text-on-surface-variant">{subtitle}</p>
    )}
  </header>
);

const ModalBody: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children, className,
}) => (
  <div className={`flex flex-col gap-5 ${className ?? ''}`.trim()}>{children}</div>
);

const ModalFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <footer className="mt-1 flex justify-end gap-3 max-sm:flex-col-reverse max-sm:[&>*]:w-full">
    {children}
  </footer>
);

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
